import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { PERMISSIONS } from "../../constants";
import { validateDocument, getDocumentSignedUrl } from "../../services/backend/ApplicantService";
import {
  getApplicantConfig,
  DEFAULT_APPLICANT_CONFIG,
  labelFor,
  profileDocLabel,
} from "../../services/backend/ApplicantConfigService";
import { normalizeDoc } from "./applicantLogic";
import { DOC_FILE_STATUS_LABELS, labelForStatus } from "./applicantLabels";
import { fileIdentity } from "./identityCheck";
import { StaffIdentityChip } from "./IdentityNotice";

const DOC_ESTADO_COLOR = {
  PENDING: "default",
  UPLOADED: "info",
  VALIDATED: "success",
  REJECTED: "error",
  VENCIDO: "warning",
};

function formatDate(v) {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
}

// Fecha Y hora de la decisión. La hora importa porque en un mismo día se validan
// muchos documentos y el sello es lo que permite ordenarlos. Zona horaria fijada
// a Colombia (validatedAt se guarda en UTC), igual que la bitácora.
function formatDateTime(v) {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(d);
}

// Quién decidió sobre el documento y cuándo. Sin esto, un documento validado no
// dice quién lo aprobó: sólo quedaba el uid guardado, que la ficha no mostraba.
// Los archivos decididos antes de que se guardara el nombre sólo llevan fecha.
function decisionStamp(info) {
  if (!info || (info.status !== "VALIDATED" && info.status !== "REJECTED")) return null;
  if (!info.validatedAt) return null;
  const verbo = info.status === "VALIDATED" ? "Validado" : "Rechazado";
  const quien = info.validatedByName ? ` por ${info.validatedByName}` : "";
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      {verbo}{quien} · {formatDateTime(info.validatedAt)}
    </Typography>
  );
}

// Document-by-document validation. The gestor approves or rejects each uploaded
// file (a document type may hold several); rejection requires a comment.
// `documents` is the map being reviewed and `scope` says where it lives:
// "application" (the postulación selected by `callId`) or "profile" (person-level
// documents — identity, and the certificate of one formación / experiencia).
// `title` null renders the table bare, for embedding next to an entry.
// Labels come from config/applicants.
export default function ApplicantDocsValidation({
  nuip,
  documents,
  callId,
  onChange,
  scope = "application",
  title = "Documentación",
  emptyText = "Sin documentos cargados.",
  // { nuip, firstName, lastName } — to flag files that seem to be someone else's.
  person = null,
}) {
  const user = useSelector((state) => state.user.value);
  const perms = user?.permissions || [];
  const canValidate = perms.includes(PERMISSIONS.APPLICANT.VALIDATE_DOC);

  const [cfg, setCfg] = useState(DEFAULT_APPLICANT_CONFIG);
  const [busyKey, setBusyKey] = useState(null); // `${type}#${fileId}`
  const [approveTarget, setApproveTarget] = useState(null); // { type, fileId, label, status }
  const [rejectTarget, setRejectTarget] = useState(null); // { type, fileId, label, status }
  const [rejectComment, setRejectComment] = useState("");
  const [rejectStep, setRejectStep] = useState("form"); // "form" -> "confirm"
  const [toast, setToast] = useState(null);

  useEffect(() => { getApplicantConfig().then(setCfg); }, []);

  const isProfile = scope === "profile";
  const labelOf = (type) => (isProfile ? profileDocLabel(cfg, type) : labelFor(cfg, type));

  // Flatten documents into one row per uploaded file.
  const rows = [];
  Object.entries(documents || {}).forEach(([type, entry]) => {
    const files = normalizeDoc(entry).files;
    files.forEach((a, i) => {
      rows.push({
        type,
        fileId: a.id || null,
        label: labelOf(type) + (files.length > 1 ? ` (${i + 1}/${files.length})` : ""),
        file: a,
      });
    });
  });

  const keyOf = (row) => `${row.type}#${row.fileId || ""}`;

  const closeReject = () => { setRejectTarget(null); setRejectComment(""); setRejectStep("form"); };

  const handleApprove = async () => {
    const { type, fileId } = approveTarget;
    setBusyKey(`${type}#${fileId || ""}`);
    const res = await validateDocument(nuip, type, "VALIDATED", user, null, callId, fileId, scope);
    setBusyKey(null);
    setApproveTarget(null);
    // `res` es { documents, documentationStatus } ya recalculado por el backend:
    // se lo pasamos al padre para que actualice su estado sin recargar la ficha.
    if (res) { setToast({ severity: "success", msg: "Documento validado." }); onChange?.(res); }
    else setToast({ severity: "error", msg: "No fue posible validar el documento." });
  };

  const handleReject = async () => {
    if (rejectComment.trim().length < 5) {
      setToast({ severity: "warning", msg: "Indica el motivo del rechazo (mínimo 5 caracteres)." });
      return;
    }
    const { type, fileId } = rejectTarget;
    setBusyKey(`${type}#${fileId || ""}`);
    const res = await validateDocument(nuip, type, "REJECTED", user, rejectComment.trim(), callId, fileId, scope);
    setBusyKey(null);
    closeReject();
    if (res) { setToast({ severity: "success", msg: "Documento rechazado. Se notificó al aspirante por correo." }); onChange?.(res); }
    else setToast({ severity: "error", msg: "No fue posible rechazar el documento." });
  };

  const handleView = async (row) => {
    const url = await getDocumentSignedUrl(nuip, row.type, user, callId, row.fileId, scope);
    if (url) window.open(url, "_blank", "noopener");
    else setToast({ severity: "error", msg: "No fue posible abrir el documento." });
  };

  // `title` null → bare table, embedded under the entry it belongs to.
  const Shell = title ? Paper : React.Fragment;
  const shellProps = title ? { sx: { p: 3 } } : {};

  return (
    <Shell {...shellProps}>
      {title && (
        <>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
      ) : (
        <Table size="small">
          <TableHead sx={{ "& .MuiTableCell-head": { fontWeight: 700 } }}>
            <TableRow>
              <TableCell>Documento</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Cargado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const info = row.file;
              const busy = busyKey === keyOf(row);
              return (
                <TableRow key={keyOf(row)}>
                  <TableCell sx={{ maxWidth: 320 }}>
                    {row.label}
                    {info?.name && (
                      <Tooltip title={info.name}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          noWrap
                          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {info.name}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={labelForStatus(DOC_FILE_STATUS_LABELS, info?.status)} color={DOC_ESTADO_COLOR[info?.status] || "default"} size="small" />
                    {info?.status === "REJECTED" && info?.rejectionComments && (
                      <Typography variant="caption" color="error" sx={{ display: "block" }}>{info.rejectionComments}</Typography>
                    )}
                    {decisionStamp(info)}
                    <StaffIdentityChip identity={fileIdentity(info, person)} />
                  </TableCell>
                  <TableCell>{formatDate(info?.uploadedAt)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Ver documento">
                        <span>
                          <IconButton size="small" onClick={() => handleView(row)} disabled={!info?.path}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {canValidate && (
                        <>
                          <Tooltip title={info?.status === "REJECTED" ? "Validar (revierte el rechazo)" : "Validar"}>
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setApproveTarget({ type: row.type, fileId: row.fileId, label: row.label, status: info?.status })}
                                disabled={busy || info?.status === "VALIDATED"}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* A validated document is a closed decision: rechazar is
                              off the table (mirrored server-side). The opposite
                              direction stays open to undo a mistaken rejection. */}
                          <Tooltip title={info?.status === "VALIDATED" ? "No se puede rechazar un documento ya validado" : "Rechazar"}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setRejectTarget({ type: row.type, fileId: row.fileId, label: row.label, status: info?.status })}
                                disabled={busy || info?.status === "VALIDATED"}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!approveTarget} onClose={() => setApproveTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Validar documento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Confirmas que <strong>{approveTarget?.label}</strong> cumple los requisitos?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Una vez validado no podrá rechazarse.
            {approveTarget?.status === "REJECTED" && <span> Se revertirá el rechazo y se borrará el motivo registrado.</span>}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveTarget(null)}>Cancelar</Button>
          <Button color="success" variant="contained" onClick={handleApprove} disabled={!!busyKey}>Validar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectTarget} onClose={closeReject} fullWidth maxWidth="sm">
        <DialogTitle>{rejectStep === "form" ? "Rechazar documento" : "¿Confirmar el rechazo?"}</DialogTitle>
        <DialogContent>
          {rejectStep === "form" ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Indica el motivo del rechazo. El aspirante deberá volver a cargar el documento.
              </DialogContentText>
              <TextField autoFocus fullWidth multiline minRows={3} label="Motivo del rechazo" value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} />
            </>
          ) : (
            <>
              <DialogContentText>
                Se rechazará <strong>{rejectTarget?.label}</strong> y se enviará un correo al aspirante
                con este motivo:
              </DialogContentText>
              <Alert severity="error" sx={{ mt: 2 }}>{rejectComment.trim()}</Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {rejectStep === "form" ? (
            <>
              <Button onClick={closeReject}>Cancelar</Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => {
                  if (rejectComment.trim().length < 5) {
                    setToast({ severity: "warning", msg: "Indica el motivo del rechazo (mínimo 5 caracteres)." });
                    return;
                  }
                  setRejectStep("confirm");
                }}
              >
                Continuar
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setRejectStep("form")} disabled={!!busyKey}>Volver</Button>
              <Button color="error" variant="contained" onClick={handleReject} disabled={!!busyKey}>Rechazar</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast && (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: "100%" }}>{toast.msg}</Alert>
        )}
      </Snackbar>
    </Shell>
  );
}
